<template>
  <div class="dashboard-root">
    <div class="dashboard-header">
      <h2 class="dashboard-title">All Tokens</h2>
      <p class="dashboard-subtitle">Overview of all access tokens</p>
    </div>

    <template v-if="loading">
      <div class="loading-container">
        <b-spinner style="width: 3rem; height: 3rem" type="grow"></b-spinner>
        <p class="loading-text">Loading...</p>
      </div>
    </template>

    <div class="d-flex justify-content-end mb-3 px-3">
      <b-button type="button" @click="handleNewToken" class="action-button">
        <b-icon icon="plus-circle" class="mr-2"></b-icon>
        New Token
      </b-button>
    </div>

    <div v-if="!loading" class="table-wrapper">
      <div class="table-controls">
        <b-form-input
          v-model="searchQuery"
          type="search"
          placeholder="Search tokens by name, status, or dates"
          class="search-input"
        />
        <div class="sort-controls">
          <b-form-select
            v-model="sortBy"
            :options="sortableFieldOptions"
            class="sort-select"
          />
          <b-form-select
            v-model="sortDirection"
            :options="sortDirectionOptions"
            class="sort-select"
          />
        </div>
      </div>
      <b-table
        :fields="fields"
        :items="items"
        hover
        class="data-table"
        responsive
        :tbody-tr-class="rowClass"
        :no-border-collapse="true"
        :filter="searchQuery"
        :filter-included-fields="filterFields"
        :sort-by="sortBy"
        :sort-desc="sortDirection === 'desc'"
        :sort-compare="sortCompare"
        label-sort-asc=""
        label-sort-desc=""
        label-sort-clear=""
        @row-clicked="toggleRow"
      >
        <template #cell(expand)="">
          <b-icon icon="chevron-right" class="expand-icon" />
        </template>

        <template #head(name)="data">
          <div class="sortable-head">
            <span>{{ data.label }}</span>
            <b-icon :icon="sortIconFor('name')" class="sort-head-icon" />
          </div>
        </template>

        <template #head(generatedOn)="data">
          <div class="sortable-head">
            <span>{{ data.label }}</span>
            <b-icon :icon="sortIconFor('generatedOn')" class="sort-head-icon" />
          </div>
        </template>

        <template #head(expirationDate)="data">
          <div class="sortable-head">
            <span>{{ data.label }}</span>
            <b-icon
              :icon="sortIconFor('expirationDate')"
              class="sort-head-icon"
            />
          </div>
        </template>

        <template #head(status)="data">
          <div class="sortable-head">
            <span>{{ data.label }}</span>
            <b-icon :icon="sortIconFor('status')" class="sort-head-icon" />
          </div>
        </template>

        <template #head(createdAt)="data">
          <div class="sortable-head">
            <span>{{ data.label }}</span>
            <b-icon :icon="sortIconFor('createdAt')" class="sort-head-icon" />
          </div>
        </template>

        <template #cell(status)="data">
          <span class="status-badge" :class="getStatusClass(data.value)">
            {{ data.value }}
          </span>
        </template>

        <template #row-details="row">
          <div class="details-wrapper">
            <div class="details-grid">
              <div class="detail-item">
                <span class="detail-label">Token:</span>
                <div class="token-wrapper" @click="toggleToken(row.item._id)">
                  <span
                    v-if="visibleTokens[row.item._id]"
                    class="detail-value token-value"
                  >
                    {{ row.item.accessToken }}
                  </span>
                  <span v-else class="token-hidden"> Click to show token </span>
                  <b-button
                    @click.stop="copyToken(row.item.accessToken)"
                    size="sm"
                    class="copy-btn"
                    v-b-tooltip.hover
                    title="Copy token"
                  >
                    <b-icon icon="clipboard" />
                  </b-button>
                </div>
              </div>

              <div class="detail-item">
                <span class="detail-label">Proxy Details:</span>
                <div class="proxy-details">
                  <p>
                    <strong>Proxy Enabled:</strong>
                    <b-badge
                      :variant="
                        row.item.useProxy !== false ? 'success' : 'secondary'
                      "
                      class="ml-2"
                    >
                      {{ row.item.useProxy !== false ? "Yes" : "No" }}
                    </b-badge>
                  </p>
                  <template v-if="row.item.useProxy !== false">
                    <p>
                      <strong>Protocol:</strong>
                      {{ row.item.proxy?.protocol || "N/A" }}
                    </p>
                    <p>
                      <strong>Host:</strong> {{ row.item.proxy?.host || "N/A" }}
                    </p>
                    <p>
                      <strong>Port:</strong> {{ row.item.proxy?.port || "N/A" }}
                    </p>
                    <p>
                      <strong>Username:</strong>
                      {{ row.item.proxy?.auth?.username || "N/A" }}
                    </p>
                    <p>
                      <strong>Password:</strong>
                      <span
                        class="password-wrapper"
                        @click="togglePassword(row.item._id)"
                      >
                        <span v-if="visiblePasswords[row.item._id]">
                          {{ row.item.proxy?.auth?.password || "N/A" }}
                        </span>
                        <span v-else class="password-hidden">
                          Click to show password
                        </span>
                      </span>
                    </p>
                  </template>
                  <p v-else class="text-muted">
                    <em
                      >Proxy is disabled for this token. Jobs will use direct
                      connection.</em
                    >
                  </p>
                </div>
              </div>

              <div class="detail-item">
                <span class="detail-label">Dates:</span>
                <div class="dates-info">
                  <p>
                    <strong>Created:</strong>
                    {{ formatDate(row.item.createdOn) }}
                  </p>
                  <!-- <p>
                    <strong>Expires:</strong>
                    {{ row.item.expirationDate }}
                  </p> -->
                </div>
              </div>
            </div>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<style scoped>
.dashboard-root {
  background: #f8f8f8;
  padding: 25px;
  border-radius: 5px;
  font-family: "Inter", "Segoe UI", Arial, sans-serif;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: left;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #343a40;
  margin-bottom: 0.5rem;
}

.dashboard-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.loading-text {
  color: #6c757d;
  margin-top: 1rem;
}

.table-wrapper {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

.table-controls {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem 0;
  flex-wrap: wrap;
}

.search-input {
  max-width: 420px;
  flex: 1;
  min-width: 260px;
}

.sort-controls {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.sort-select {
  min-width: 160px;
}

.sortable-head {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.sort-head-icon {
  color: #ffffff;
  font-size: 0.9rem;
}

.data-table th {
  background-color: #f8f9fa;
  color: #495057;
  font-weight: 600;
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.data-table tbody tr:hover {
  background-color: #f1f5f9 !important;
  cursor: pointer;
}

.action-button {
  background: #0077ff !important;
  border: none;
  color: white !important;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border-radius: 8px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  transition: all 0.2s;
}

.action-button:hover {
  background: #0066dd !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 119, 255, 0.15);
}

.status-badge {
  display: inline-block;
  padding: 0.4rem 0.7rem;
  border-radius: 1rem;
  font-weight: 500;
  font-size: 0.9rem;
}

.status-valid {
  background-color: #d1fae5;
  color: #1a7f37;
}

.status-expired {
  background-color: #fbeaea;
  color: #c81e1e;
}

.details-wrapper {
  padding: 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-label {
  font-weight: 600;
  color: #4b5563;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.token-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #fff;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-wrap: wrap;
}

.token-value {
  font-family: monospace;
  color: #0077ff;
  font-size: 0.9rem;
  word-break: break-all;
  flex: 1;
  min-width: 0;
}

.token-hidden {
  color: #6b7280;
  font-size: 0.9rem;
  font-style: italic;
}

.copy-btn {
  padding: 0.25rem 0.5rem;
  background: #f1f5f9;
  border: none;
  color: #64748b;
}

.copy-btn:hover {
  background: #e2e8f0;
  color: #0077ff;
}

.proxy-details,
.dates-info {
  background: #fff;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.proxy-details p,
.dates-info p {
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  color: #4b5563;
}

.expand-btn {
  background: transparent;
  border: none;
  color: #64748b;
  padding: 0.25rem;
}

.expand-btn:hover {
  color: #0077ff;
}

.table-row {
  transition: background-color 0.2s ease;
}

.table-row.expanded {
  background-color: #f8fafc;
}

.expand-cell {
  width: 40px;
  text-align: center;
  vertical-align: middle;
}

.expand-header {
  width: 40px;
}

.expand-icon {
  color: #64748b;
  font-size: 1.2rem;
  transition: transform 0.2s ease;
}

.expanded .expand-icon {
  transform: rotate(90deg);
}

.password-wrapper {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  background: #f8fafc;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 0.5rem;
}

.password-wrapper:hover {
  background: #f1f5f9;
}

.password-hidden {
  color: #6b7280;
  font-style: italic;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .dashboard-root {
    padding: 1rem;
  }

  .dashboard-title {
    font-size: 1.7rem;
  }

  .table-wrapper {
    padding: 1rem;
  }

  .data-table th,
  .data-table td {
    padding: 0.75rem;
  }
}
</style>

<script>
import router from "@/router";
import axios from "axios";
import moment from "moment";

export default {
  data() {
    return {
      loading: true,
      searchQuery: "",
      sortBy: "createdAt",
      sortDirection: "desc",
      items: [],
      visibleTokens: {},
      visiblePasswords: {},
      filterFields: ["name", "status", "generatedOn", "expirationDate", "createdAt"],
      sortableFieldOptions: [
        { value: "name", text: "Sort: Name" },
        { value: "status", text: "Sort: Status" },
        { value: "generatedOn", text: "Sort: Generated On" },
        { value: "expirationDate", text: "Sort: Expiration Date" },
        { value: "createdAt", text: "Sort: Created On" },
      ],
      sortDirectionOptions: [
        { value: "desc", text: "Descending" },
        { value: "asc", text: "Ascending" },
      ],
      fields: [
        {
          key: "expand",
          label: "",
          tdClass: "expand-cell",
          thClass: "expand-header",
        },
        { key: "name", sortable: true },
        { key: "generatedOn", sortable: true },
        {
          key: "expirationDate",
          label: "Expiration Date",
          sortable: true,
        },
        { key: "status", sortable: true },
        {
          key: "createdAt",
          label: "Created On",
          sortable: true,
        },
      ],
    };
  },
  methods: {
    handleNewToken() {
      this.$router.push("/new-token");
    },
    toggleToken(id) {
      this.$set(this.visibleTokens, id, !this.visibleTokens[id]);
    },
    togglePassword(id) {
      this.$set(this.visiblePasswords, id, !this.visiblePasswords[id]);
    },
    formatNumber(num) {
      return new Intl.NumberFormat().format(num);
    },
    getStatusClass(status) {
      return {
        "status-valid": status === "Valid",
        "status-expired": status === "Expired",
      };
    },
    formatDate(date) {
      return date ? moment(date).format("DD-MM-YYYY hh:mm A") : "N/A";
    },
    copyToken(token) {
      navigator.clipboard.writeText(token);
      this.$toast.success("Token copied to clipboard!");
    },
    toggleRow(row, index, event) {
      if (event.target.tagName.toLowerCase() === "button") return; // Ignore if clicking buttons
      this.$set(row, "_showDetails", !row._showDetails);
    },
    rowClass(item, type) {
      if (!item || type !== "row") return;
      return {
        "table-row": true,
        expanded: item._showDetails,
      };
    },
    sortCompare(aRow, bRow, key) {
      if (["generatedOn", "expirationDate", "createdAt"].includes(key)) {
        const a = aRow[`${key}Raw`] || 0;
        const b = bRow[`${key}Raw`] || 0;
        if (a === b) return 0;
        return a > b ? 1 : -1;
      }
      const aValue = (aRow[key] || "").toString().toLowerCase();
      const bValue = (bRow[key] || "").toString().toLowerCase();
      return aValue.localeCompare(bValue);
    },
    sortIconFor(fieldKey) {
      if (this.sortBy !== fieldKey) return "arrow-down-up";
      return this.sortDirection === "desc" ? "arrow-down" : "arrow-up";
    },
  },
  async mounted() {
    try {
      const response = await axios.get("/api/tokens/all"); // Replace with your API URL
      const _itemsAll = response.data.data;
      this.items = _itemsAll.map((item) => {
        const createdAtRaw = item?.createdAt ? new Date(item.createdAt).getTime() : null;
        const generatedOnRaw = item?.generatedOn
          ? new Date(item.generatedOn).getTime()
          : null;
        const expirationDateRaw = item?.expirationDate
          ? new Date(item.expirationDate).getTime()
          : null;

        return {
          ...item,
          host: item?.proxy?.host,
          host: item?.proxy?.port,
          createdAtRaw,
          generatedOnRaw,
          expirationDateRaw,
          status: moment(item?.expirationDate).isBefore(Date.now())
            ? "Expired"
            : "Valid",
          expirationDate:
            item?.expirationDate &&
            moment(item?.expirationDate).format("DD-MM-YYYY hh:mm A") +
              " ( UK Time )",
          generatedOn:
            item?.generatedOn &&
            moment(item?.generatedOn).format("DD-MM-YYYY hh:mm A"),
          createdAt:
            item?.createdAt &&
            moment(item?.createdAt).format("DD-MM-YYYY hh:mm A"),
        };
      });
    } catch {
      this.$toast.error("Failed to load data");
    } finally {
      this.loading = false;
    }
  },
};
</script>
